import { Input, Button, ErrorNotice, Field, Modal } from '@kiipu/ui';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Popover } from '@base-ui/react/popover';
import { ListBullets, X } from '@phosphor-icons/react';

import { api, type ItemDetail } from '../api';
import { useToast } from '../context';
import {
  defaultReaderPreferences,
  parseReaderPreferences,
  type ReaderPreferences,
} from '../hooks/reader-position-helpers';
import './reader.css';
import { canMutate, usePlatform } from '../lib/client-platform';
import { useDesktopDirty } from '../lib/desktop-dirty-state';

export type ReaderHeading = { id: string; text: string; level: number; depth: number };

export function readerHeadings(html: string): ReaderHeading[] {
  const elements = [
    ...new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6'),
  ].filter((heading) => heading.id && heading.textContent?.trim());
  const minimum = Math.min(...elements.map((heading) => Number(heading.tagName.slice(1))));
  return elements.map((heading) => ({
    id: heading.id,
    text: heading.textContent!.trim(),
    level: Number(heading.tagName.slice(1)),
    depth: Number(heading.tagName.slice(1)) - minimum,
  }));
}

export function useReaderPreferences(userId: string) {
  const [preferences, setPreferences] = useState<ReaderPreferences>({
    ...defaultReaderPreferences,
  });
  const [storageFailed, setStorageFailed] = useState(false);
  const key = `kiipu.reader-preferences.v1:${userId}`;
  useLayoutEffect(() => {
    try {
      setPreferences(parseReaderPreferences(localStorage.getItem(key)));
      setStorageFailed(false);
    } catch {
      setPreferences({ ...defaultReaderPreferences });
      setStorageFailed(true);
    }
  }, [key]);
  function change(next: ReaderPreferences) {
    setPreferences(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
      setStorageFailed(false);
    } catch {
      setStorageFailed(true);
    }
  }
  return { preferences, change, storageFailed };
}

export function useCurrentHeading(
  scrollRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  headings: ReaderHeading[],
  documentKey: string,
) {
  const [active, setActive] = useState('');
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content || !headings.length) {
      setActive('');
      return;
    }
    let frame = 0;
    const measure = () => {
      const top = scroller.getBoundingClientRect().top + 64;
      let current = headings[0]!.id;
      for (const heading of headings) {
        if (
          (content.querySelector<HTMLElement>(`#${heading.id}`)?.getBoundingClientRect().top ??
            Infinity) <= top
        )
          current = heading.id;
      }
      setActive(current);
    };
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(update);
    observer.observe(content);
    scroller.addEventListener('scroll', update, { passive: true });
    measure();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      scroller.removeEventListener('scroll', update);
    };
  }, [scrollRef, contentRef, headings, documentKey]);
  return active;
}

function ReaderPopover({
  label,
  icon,
  children,
  dialog,
}: {
  label: string;
  icon: ReactNode;
  children: (close: () => void) => ReactNode;
  dialog?: { open: boolean; onOpenChange: (open: boolean) => void };
}) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 640px)');
    const update = () => setMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  if (dialog)
    return (
      <Modal
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title={label}
        className="reader-controls-dialog"
      >
        {children(() => dialog.onOpenChange(false))}
      </Modal>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={mobile}>
      <Popover.Trigger
        render={
          <button
            type="button"
            className="icon-button reader-control-trigger"
            aria-label={label}
            title={label}
          />
        }
      >
        {icon}
      </Popover.Trigger>
      <Popover.Portal>
        {mobile ? <Popover.Backdrop className="reader-popover-backdrop" /> : null}
        <Popover.Positioner
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={12}
          className="reader-popover-positioner"
          data-sheet={mobile}
        >
          <Popover.Popup className="reader-control-popup">
            <div className="reader-control-heading">
              <Popover.Title>{label}</Popover.Title>
              <Popover.Close
                render={
                  <button type="button" className="icon-button" aria-label={`关闭${label}`} />
                }
              >
                <X size={17} />
              </Popover.Close>
            </div>
            {children(() => setOpen(false))}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function ReaderOutline({
  headings,
  active,
  onChoose,
  dialog,
}: {
  headings: ReaderHeading[];
  active: string;
  onChoose: (id: string) => void;
  dialog?: { open: boolean; onOpenChange: (open: boolean) => void };
}) {
  if (headings.length < 2) return null;
  return (
    <ReaderPopover label="文章目录" icon={<ListBullets size={17} />} dialog={dialog}>
      {(close) => (
        <nav aria-label="文章章节" className="reader-outline">
          {headings.map((heading) => (
            <button
              type="button"
              key={heading.id}
              aria-current={active === heading.id ? 'location' : undefined}
              style={{ paddingInlineStart: `${10 + heading.depth * 12}px` }}
              onClick={() => {
                close();
                requestAnimationFrame(() => onChoose(heading.id));
              }}
            >
              {heading.text}
            </button>
          ))}
        </nav>
      )}
    </ReaderPopover>
  );
}

export function ReaderTypography({
  preferences,
  onChange,
  storageFailed,
  dialog,
}: {
  preferences: ReaderPreferences;
  onChange: (value: ReaderPreferences) => void;
  storageFailed: boolean;
  dialog?: { open: boolean; onOpenChange: (open: boolean) => void };
}) {
  return (
    <ReaderPopover
      label="阅读设置"
      dialog={dialog}
      icon={
        <span className="reader-aa" aria-hidden="true">
          Aa
        </span>
      }
    >
      {() => (
        <div className="reader-typography-controls">
          <fieldset>
            <legend>字号</legend>
            <div className="reader-type-segments">
              {(
                [
                  { value: 'small', label: '小' },
                  { value: 'standard', label: '标准' },
                  { value: 'large', label: '大' },
                ] as const
              ).map((size) => (
                <label key={size.value} data-selected={preferences.size === size.value}>
                  <Input
                    type="radio"
                    name="reader-size"
                    value={size.value}
                    checked={preferences.size === size.value}
                    onChange={() => onChange({ ...preferences, size: size.value })}
                  />
                  <span>{size.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>正文宽度</legend>
            <div className="reader-type-segments">
              {(
                [
                  { value: 'standard', label: '标准宽度' },
                  { value: 'wide', label: '宽版' },
                ] as const
              ).map((width) => (
                <label key={width.value} data-selected={preferences.width === width.value}>
                  <Input
                    type="radio"
                    name="reader-width"
                    value={width.value}
                    checked={preferences.width === width.value}
                    onChange={() => onChange({ ...preferences, width: width.value })}
                  />
                  <span>{width.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="field-hint">排版偏好保存在当前浏览器的此账户下。</p>
          {storageFailed ? (
            <p className="reader-storage-hint" role="status">
              浏览器无法保留偏好，本次调整仍然有效。
            </p>
          ) : null}
        </div>
      )}
    </ReaderPopover>
  );
}

export function RenameTitleDialog({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const [title, setTitle] = useState(item.title);
  usePlatform();
  useDesktopDirty(`rename:${item.id}`, title !== item.title);
  const mutationId = useRef(crypto.randomUUID());
  const client = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () => api.patch(item.id, { title: title.trim(), mutationId: mutationId.current }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['items'] });
      void client.invalidateQueries({ queryKey: ['item', item.id] });
      toast('标题已更新');
      onClose();
    },
  });
  return (
    <Modal
      open
      title="修改标题"
      description="为内容取一个便于找回的标题。"
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (title.trim() && !mutation.isPending) mutation.mutate();
        }}
      >
        <Field label="内容标题">
          <Input
            autoFocus
            value={title}
            maxLength={1000}
            required
            disabled={mutation.isPending || !canMutate()}
            onChange={(event) => {
              setTitle(event.target.value);
              mutationId.current = crypto.randomUUID();
              mutation.reset();
            }}
          />
        </Field>
        <ErrorNotice error={mutation.error} />
        <div className="modal-actions">
          <Button disabled={mutation.isPending} onClick={onClose}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            busy={mutation.isPending}
            disabled={!canMutate() || !title.trim() || title.trim() === item.title}
          >
            保存标题
          </Button>
        </div>
      </form>
    </Modal>
  );
}
